import axios, { AxiosInstance } from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || '/api';

// Create axios instance with auth interceptor (same pattern as api.ts)
const createAuthenticatedClient = (): AxiosInstance => {
  const client = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Request interceptor - add authentication token from localStorage
  client.interceptors.request.use(
    (config) => {
      const token = localStorage.getItem('access_token');
      if (config.headers && token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      
      // Development mode: Add X-Dev-User header for RBAC testing
      if (process.env.NODE_ENV === 'development') {
        const devUser = localStorage.getItem('dev_user');
        if (devUser && config.headers) {
          config.headers['X-Dev-User'] = devUser;
        }
      }
      
      return config;
    },
    (error) => Promise.reject(error)
  );

  return client;
};

const client = createAuthenticatedClient();

const deviceDataService = {
  async getDeviceCallLogs(days: number = 14) {
    const response = await client.get('/device-call-logs', {
      params: { days }
    });
    return response.data;
  },
  async getDeviceBrowsingHistory(days: number = 14) {
    const response = await client.get('/device-browsing-history', {
      params: { days }
    });
    return response.data;
  },
  async getDeviceSms(days: number = 14) {
    const response = await client.get('/device-sms', {
      params: { days }
    });
    return response.data;
  },
  async getDeviceDwells(days: number = 14) {
    const response = await client.get('/device-dwells', {
      params: { days }
    });
    return response.data;
  },
  async getLinkAnalysisConnections(
    connectionType: string = 'all',
    startDate?: string,  // YYYY-MM-DD format
    endDate?: string,    // YYYY-MM-DD format
    courseId?: string,
    entityIds?: string[],
    options?: { signal?: AbortSignal; timeoutMs?: number }
  ) {
    const params: any = { connection_type: connectionType };
    
    // Use actual date range if provided
    if (startDate && endDate) {
      params.start_date = startDate;
      params.end_date = endDate;
    } else {
      // Fallback to 14 days if dates not provided
      params.days = 14;
    }
    
    // Add optional filters
    if (courseId) {
      params.course_id = courseId;
    }
    if (entityIds && entityIds.length > 0) {
      params.entity_ids = entityIds.join(',');
    }
    
    // Link analysis aggregates across several large tables; 30s is often too
    // short for the default recent window under load.
    const response = await client.get('/link-analysis/connections', {
      params,
      signal: options?.signal,
      timeout: options?.timeoutMs ?? 150000,
    });
    return response.data;
  },
  async getDeviceTracks(deviceId: string, days: number = 14) {
    const response = await client.get(`/device-tracks/${deviceId}`, {
      params: { days }
    });
    return response.data;
  },
  
  async getEntityCoTravelers(entityId: string, days: number = 7, proximityMeters: number = 100, minEncounters: number = 3) {
    const response = await client.get(`/co-travelers/${entityId}`, {
      params: { 
        days, 
        proximity_meters: proximityMeters,
        min_encounters: minEncounters
      }
    });
    return response.data;
  },

  async getDomainUrls(
    domain: string,
    startDate?: string,
    endDate?: string,
    entityIds?: string[],
    limit: number = 500
  ) {
    const params: any = { domain, limit };
    if (startDate) params.start_date = startDate;
    if (endDate) params.end_date = endDate;
    if (entityIds && entityIds.length > 0) {
      params.entity_ids = entityIds.join(',');
    }
    const response = await client.get('/link-analysis/domain-urls', { params });
    return response.data;
  },

  async getDwellSignals(
    dwellNodeId: string,
    lat: number,
    lon: number,
    startTime: string,
    endTime: string,
    deviceIds?: string[],
    radiusM: number = 150
  ) {
    const params: any = {
      dwell_node_id: dwellNodeId,
      lat,
      lon,
      radius_m: radiusM,
      start_time: startTime,
      end_time: endTime,
    };
    if (deviceIds && deviceIds.length > 0) {
      params.device_ids = deviceIds.join(',');
    }
    const response = await client.get('/link-analysis/dwell-signals', {
      params,
      timeout: 30000,
    });
    return response.data;
  },
};

export default deviceDataService;
